/**
 * Sitewide scroll reveal (build log row 72).
 *
 * Anything carrying `data-reveal` is watched by one IntersectionObserver and
 * gets `data-revealed` the first time it reaches the viewport; bettino-global.css
 * turns that into the §2f entrance (opacity + translateY, long duration,
 * ease-out-slow). Each element is unobserved as soon as it fires, so the reveal
 * happens once and nothing re-animates on the way back up.
 *
 * The attribute, not a class, so a theme block or a section only has to print
 * `data-reveal` to opt in.
 *
 * Reduced motion: layout/theme.liquid sets `html[data-bettino-reveal='on']`
 * synchronously and only when the visitor has no reduced-motion preference (and
 * the browser has IntersectionObserver). Without it the CSS is inert, and this
 * module returns before creating the observer or binding a single listener, so
 * there is no transform to un-do and nothing can flash.
 *
 * Two shared opt-ins sit on top (build log row 92):
 *
 * - `data-reveal-items="<selector>"` on a container tags whatever the selector
 *   matches inside it, so a section opts a whole list in with one attribute.
 *   Outside an overlay the matches become ordinary `data-reveal` elements, but
 *   only those still below the reveal line when the module runs: anything
 *   already in the first viewport stays as it was painted, so above-the-fold
 *   content (and LCP) is never held back.
 * - `data-reveal-scope` on a dialog, drawer or disclosure makes its items
 *   (`data-reveal-item`, or tagged by a `data-reveal-items` inside it) replay
 *   the same entrance in order on every open and drop straight back on close.
 *   Open means the `open` attribute, or the class named as the attribute's
 *   value (`data-reveal-scope="menu-open"`).
 */

const REVEAL_SELECTOR = '[data-reveal]';
const REVEALED_ATTRIBUTE = 'data-revealed';
const INDEX_PROPERTY = '--bettino-reveal-index';

const ITEMS_ATTRIBUTE = 'data-reveal-items';
const ITEM_ATTRIBUTE = 'data-reveal-item';
const SCOPE_SELECTOR = '[data-reveal-scope]';

/** Set once the overlay reveal is wired, so without this module nothing in an overlay is ever hidden. */
const READY_ATTRIBUTE = 'data-bettino-reveal-ready';

/** Keyframes name of the CSS failsafe in bettino-global.css. */
const FAILSAFE_ANIMATION = 'bettino-reveal-failsafe';

/** Section boundary: the stagger never reaches across one. */
const SECTION_SELECTOR = '[id^="shopify-section"], .shopify-section';

/** Fire a little before the element is fully in view. */
const ROOT_MARGIN = '0px 0px -10% 0px';

/** @type {IntersectionObserver | null} */
let observer = null;

/** @param {Element} element @returns {boolean} */
function carriesReveal(element) {
  return element.matches(REVEAL_SELECTOR) || element.querySelector(REVEAL_SELECTOR) !== null;
}

/**
 * The element's position in its own row of reveals.
 *
 * Siblings are the common case, but a row is not always a flat one: the
 * bestsellers carousel wraps every card in its own `<slideshow-slide>`, so a
 * card's parent holds exactly one reveal. Single-reveal wrappers are climbed
 * through until a level with more than one is found, and the climb stops at the
 * section, so two sections that each reveal one thing never stagger each other.
 *
 * @param {Element} element
 * @returns {number}
 */
function revealIndex(element) {
  let node = element;

  while (node.parentElement && !node.matches(SECTION_SELECTOR)) {
    const peers = Array.from(node.parentElement.children).filter(carriesReveal);
    if (peers.length > 1) return peers.indexOf(node);
    node = node.parentElement;
  }

  return 0;
}

/** @param {IntersectionObserverEntry[]} entries */
function onIntersect(entries) {
  for (const entry of entries) {
    if (!entry.isIntersecting) continue;
    observer?.unobserve(entry.target);
    entry.target.setAttribute(REVEALED_ATTRIBUTE, '');
  }
}

/** @param {ParentNode & Element | Document} root */
function observe(root) {
  if (!observer) return;

  const elements = Array.from(root.querySelectorAll(REVEAL_SELECTOR));
  if (root instanceof Element && root.matches(REVEAL_SELECTOR)) elements.unshift(root);

  for (const element of elements) {
    if (element.hasAttribute(REVEALED_ATTRIBUTE)) continue;

    const index = revealIndex(element);
    if (index > 0) {
      /** @type {HTMLElement} */ (element).style.setProperty(INDEX_PROPERTY, `${index}`);
    }

    observer.observe(element);

    // Something is waiting again, so the stall sweep has work to do even if it
    // stood itself down earlier (Load More, infinite scroll, the editor).
    armSweep();
  }
}

/**
 * Safety net for a registration the observer has stopped reporting on.
 *
 * A lazy section that finishes rendering below an already-queued element
 * leaves that element's observation stale. The PDP's `product-recommendations`
 * fetches its cards long after load and grows by ~600px; the heading queued
 * above it gets one callback at page load saying "not intersecting" and then
 * is never reported again, even as it scrolls right through the viewport,
 * while a second observer created later on the very same node fires correctly.
 * That silently swallowed the "From the Same Atelier" heading — it sat at
 * opacity 0 for the whole pageview.
 *
 * This sweep answers the observer's own question synchronously instead of
 * waiting to be told: anything still unrevealed whose top has already reached
 * the reveal line has been passed, so reveal it. It deliberately does not
 * re-register anything — unobserving and re-observing on every mutation drops
 * the notifications already queued for healthy elements and stalls the whole
 * page — and it never touches an element that is still below the line, which
 * is left to the observer as normal.
 *
 * The line is the viewport height less the 10% the observer's bottom
 * rootMargin trims, so a swept element reveals exactly where an observed one
 * would have.
 */
function revealPassedPending() {
  if (!observer) return 0;

  const pending = document.querySelectorAll(`${REVEAL_SELECTOR}:not([${REVEALED_ATTRIBUTE}])`);
  const line = window.innerHeight * 0.9;
  // Measure everything before writing: a write between reads forces a layout per element.
  const passed = Array.from(pending).filter((element) => element.getBoundingClientRect().top < line);

  for (const element of passed) {
    observer.unobserve(element);
    element.setAttribute(REVEALED_ATTRIBUTE, '');
  }

  return pending.length - passed.length;
}

/**
 * Collection/search "Load More" and infinite scroll append cards with a plain
 * `grid.append(...)` (paginated-list.js, bettino-load-more.js) — no
 * `shopify:section:load` fires for either, so appended cards need their own
 * watcher or they'd carry `data-reveal` and never be observed.
 */
function watchForAppendedContent() {
  const mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        tagItems(node);
        registerScopes(node);
        observe(node);
      }
    }
  });

  mutationObserver.observe(document.body, { childList: true, subtree: true });
}

/** @type {boolean} */
let sweeping = false;

/** @type {boolean} */
let sweepQueued = false;

function onSweepScroll() {
  if (sweepQueued) return;
  sweepQueued = true;

  requestAnimationFrame(() => {
    sweepQueued = false;

    // Nothing left to watch: drop the listener rather than run every frame for
    // the rest of the page's life. `armSweep` puts it back when new elements
    // arrive, which is what Load More and infinite scroll do.
    if (revealPassedPending() === 0) {
      window.removeEventListener('scroll', onSweepScroll, { capture: true });
      sweeping = false;
    }
  });
}

/**
 * Run the sweep while the page scrolls. Passive and rAF-throttled, so at worst
 * it costs one `getBoundingClientRect` per pending element per frame, on a
 * list that only shrinks.
 *
 * Capture phase: this theme locks `html`/`body` overflow and scrolls an inner
 * `.page-wrapper`, and a scroll event does not bubble, so a listener on window
 * only hears it on the way down.
 */
function armSweep() {
  if (sweeping) return;

  sweeping = true;
  window.addEventListener('scroll', onSweepScroll, { capture: true, passive: true });
}

/* ---- Shared opt-in: data-reveal-items ---- */

/**
 * Tag every match of each `data-reveal-items` container at or inside `root`,
 * and every container a newly added node landed in (Load More, a cart
 * re-render, predictive results).
 *
 * @param {Element | Document} root
 */
function tagItems(root) {
  const containers = new Set(root.querySelectorAll(`[${ITEMS_ATTRIBUTE}]`));
  if (root instanceof Element) {
    const host = root.closest(`[${ITEMS_ATTRIBUTE}]`);
    if (host) containers.add(host);
  }

  const line = window.innerHeight * 0.9;
  /** @type {Element[]} */
  const toReveal = [];

  for (const container of containers) {
    const selector = container.getAttribute(ITEMS_ATTRIBUTE);
    if (!selector) continue;

    const inOverlay = container.closest(SCOPE_SELECTOR) !== null;

    // `:scope` alone names the container itself (one block revealed whole).
    const matches = Array.from(container.querySelectorAll(selector));
    if (container.matches(selector)) matches.unshift(container);

    for (const element of matches) {
      if (inOverlay) {
        if (!element.hasAttribute(ITEM_ATTRIBUTE)) element.setAttribute(ITEM_ATTRIBUTE, '');
        continue;
      }

      // Already painted in view (or not rendered at all): leave it be.
      if (element.hasAttribute('data-reveal') || element.hasAttribute(REVEALED_ATTRIBUTE)) continue;
      const rect = element.getBoundingClientRect();
      if (rect.top < line || (rect.width === 0 && rect.height === 0)) continue;

      toReveal.push(element);
    }
  }

  // Written after every measurement, so the reads above share one layout.
  for (const element of toReveal) element.setAttribute('data-reveal', '');
}

/* ---- Overlay reveal: data-reveal-scope ---- */

/** @type {WeakMap<Element, boolean>} last seen open state per scope */
const scopeOpen = new WeakMap();

/** @type {WeakSet<Element>} items waiting on their reveal frame */
const pendingItems = new WeakSet();

/** @type {MutationObserver | null} the scope's own open / class */
let scopeStateObserver = null;

/** @type {MutationObserver | null} what happens inside the scope */
let scopeContentObserver = null;

/** @param {Element} scope */
function isOpen(scope) {
  const openClass = scope.getAttribute('data-reveal-scope');
  return openClass ? scope.classList.contains(openClass) : scope.hasAttribute('open');
}

/** @param {Element} scope @returns {Element[]} */
function ownItems(scope) {
  return Array.from(scope.querySelectorAll(`[${ITEM_ATTRIBUTE}]`)).filter(
    (item) => item.closest(SCOPE_SELECTOR) === scope
  );
}

/**
 * Stagger `items` in, in DOM order. Two frames, so the now-rendered overlay has
 * committed the hidden state before the revealed one lands and the transition
 * actually runs. Items with no box take no stagger slot.
 *
 * @param {Element} scope
 * @param {Element[]} items
 */
function play(scope, items) {
  let index = 0;

  for (const item of items) {
    if (item.getClientRects().length) {
      /** @type {HTMLElement} */ (item).style.setProperty(INDEX_PROPERTY, `${index++}`);
    }
    pendingItems.add(item);
  }

  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      const open = isOpen(scope);
      for (const item of items) {
        pendingItems.delete(item);
        if (open) item.setAttribute(REVEALED_ATTRIBUTE, '');
      }
    })
  );
}

/**
 * Bring a scope's items in line with its open state.
 *
 * Opening replays the sequence. Closing drops every item back at once (the
 * CSS gives that the short duration, no stagger). While it stays open, items
 * that arrive in new nodes stagger in; items that only lost their attributes
 * (the cart's morph re-render copies server attributes over live ones) are
 * put straight back, in the same task, so they never flicker.
 *
 * @param {Element} scope
 * @param {Node[]} [added]
 */
function syncScope(scope, added = []) {
  tagItems(scope);

  const open = isOpen(scope);
  const wasOpen = scopeOpen.get(scope) ?? false;
  scopeOpen.set(scope, open);

  const items = ownItems(scope);

  if (!open) {
    for (const item of items) item.removeAttribute(REVEALED_ATTRIBUTE);
    return;
  }

  const waiting = items.filter((item) => !item.hasAttribute(REVEALED_ATTRIBUTE) && !pendingItems.has(item));
  if (!wasOpen) {
    play(scope, waiting);
    return;
  }

  const fresh = [];
  for (const item of waiting) {
    if (added.some((node) => node.contains(item))) fresh.push(item);
    else item.setAttribute(REVEALED_ATTRIBUTE, '');
  }
  if (fresh.length) play(scope, fresh);
}

/** @param {MutationRecord[]} records */
function onScopeState(records) {
  const scopes = new Set(records.map((record) => /** @type {Element} */ (record.target)));
  for (const scope of scopes) {
    if (isOpen(scope) !== (scopeOpen.get(scope) ?? false)) syncScope(scope);
  }
}

/** @param {MutationRecord[]} records */
function onScopeContent(records) {
  /** @type {Map<Element, Node[]>} */
  const touched = new Map();

  for (const record of records) {
    const target = /** @type {Element} */ (record.target);
    const scope = target.closest(SCOPE_SELECTOR);
    if (!scope) continue;
    if (!touched.has(scope)) touched.set(scope, []);
    touched.get(scope)?.push(...record.addedNodes);
  }

  for (const [scope, added] of touched) syncScope(scope, added);
}

/**
 * Start watching every scope at or inside `root`. A scope that is already open
 * (the cart drawer restored on load) shows its items as they are, unanimated.
 *
 * @param {Element | Document} root
 */
function registerScopes(root) {
  if (!scopeStateObserver || !scopeContentObserver) return;

  const scopes = Array.from(root.querySelectorAll(SCOPE_SELECTOR));
  if (root instanceof Element && root.matches(SCOPE_SELECTOR)) scopes.unshift(root);

  for (const scope of scopes) {
    if (scopeOpen.has(scope)) continue;

    tagItems(scope);
    const open = isOpen(scope);
    scopeOpen.set(scope, open);
    if (open) for (const item of ownItems(scope)) item.setAttribute(REVEALED_ATTRIBUTE, '');

    scopeStateObserver.observe(scope, { attributes: true, attributeFilter: ['open', 'class'] });
    scopeContentObserver.observe(scope, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [ITEM_ATTRIBUTE, REVEALED_ATTRIBUTE],
    });
  }
}

/**
 * The CSS failsafe (bettino-global.css) shows `data-reveal` elements 3s after
 * they are styled if this module hasn't set READY_ATTRIBUTE yet. Setting it
 * drops that animation, so on a late run anything the failsafe already showed
 * is marked revealed first and stays visible instead of blinking out.
 */
function keepFailsafeReveals() {
  for (const element of document.querySelectorAll(REVEAL_SELECTOR)) {
    const shown = element
      .getAnimations?.()
      .some(
        (animation) =>
          'animationName' in animation &&
          animation.animationName === FAILSAFE_ANIMATION &&
          animation.playState === 'finished'
      );
    if (shown) element.setAttribute(REVEALED_ATTRIBUTE, '');
  }
}

function init() {
  if (document.documentElement.getAttribute('data-bettino-reveal') !== 'on') return;

  observer = new IntersectionObserver(onIntersect, { rootMargin: ROOT_MARGIN });
  scopeStateObserver = new MutationObserver(onScopeState);
  scopeContentObserver = new MutationObserver(onScopeContent);

  tagItems(document);
  registerScopes(document);
  keepFailsafeReveals();
  document.documentElement.setAttribute(READY_ATTRIBUTE, '');

  observe(document);
  watchForAppendedContent();

  // Theme editor: a re-rendered section brings its own un-observed elements.
  document.addEventListener('shopify:section:load', (event) => {
    const target = /** @type {any} */ (event).target;
    observe(target instanceof Element ? target : document);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
